import advisorContactReportForm from '../js/advisorContactReportForm';
import svg from '../js/svg';

const EP = 'advisors/get-lead-contact-reports';
const COL_COUNT = 9;
const PREVIEW_LEN = 60;

const HEADERS = [
  'Date of contact',
  'Medium',
  'Motivation',
  'Notes',
  'Follow-up date',
  'Follow-up action',
  'Retire lead',
  'Reason to retire',
  '',
];

function cellText(val) {
  return val != null && String(val).trim() !== '' ? String(val) : '';
}

function ynText(val) {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '—';
}

async function dateText(val) {
  if (val == null || String(val).trim() === '') return '—';
  return await U.formatDate(val);
}

function leadUuid() {
  const fromVars = (thsPage && thsPage.vars && thsPage.vars.lead_uuid) || (thsTab && thsTab.vars && thsTab.vars.lead_uuid);
  const fromDetails = thsPage && thsPage.leadDetails && thsPage.leadDetails.lead_uuid;
  return String(fromVars || fromDetails || '').trim();
}

function toggleDetailRow(detailRow) {
  if (!detailRow) return;
  detailRow.classList.toggle('hide');
}

function fillPreviewCell(cell, text, onMore) {
  const full = cellText(text);
  if (!full) {
    cell.textContent = '—';
    return;
  }
  if (full.length <= PREVIEW_LEN) {
    cell.textContent = full;
    return;
  }
  cell.appendChild(document.createTextNode(`${full.slice(0, PREVIEW_LEN)} `));
  const more = document.createElement('span');
  more.className = 'contact-report-more text-primary cursor-pointer font-medium';
  more.setAttribute('role', 'button');
  more.textContent = 'More...';
  more.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onMore();
  });
  cell.appendChild(more);
}

function addDetailBlock(parent, label, text) {
  const block = document.createElement('div');
  block.className = 'contact-report-detail-block';
  const heading = document.createElement('h6');
  heading.className = 'text-primary';
  heading.textContent = label;
  const body = document.createElement('p');
  body.className = 'whitespace-pre-wrap break-words';
  body.textContent = cellText(text) || '—';
  block.appendChild(heading);
  block.appendChild(body);
  parent.appendChild(block);
}

export default {
  async prepare() {
    const id = leadUuid();
    if (!id) return;
    this.recipe.methods.push(await O.mPOST(EP, {lead_uuid: id}));
  },

  async render() {
    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) data = await O.PREP(rec);
    await this.loadPage(data);
  },

  async loadPage(data) {
    this.mountHeaderAction();
    this.buildTableHead();
    if (!leadUuid()) {
      await this.buildTableBody([], 'No lead selected. Open a lead from the list.');
      return;
    }
    const listRes = data[EP] || {};
    if (listRes.status && listRes.status !== 'OK') {
      await this.buildTableBody([], (listRes.data && listRes.data.error) || 'Unable to load contact reports');
      return;
    }
    await this.buildTableBody(Array.isArray(listRes.data) ? listRes.data : []);
  },

  mountHeaderAction() {
    const wrap = U.id('contactReportsHeaderActions');
    if (!wrap || wrap.dataset.built === '1') return;
    wrap.dataset.built = '1';
    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn whitespace-nowrap';
    addBtn.textContent = 'Add contact report';
    addBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.openContactReportForm();
    });
    wrap.appendChild(addBtn);
  },

  async openContactReportForm() {
    const id = leadUuid();
    if (!id) return;
    await advisorContactReportForm.open({
      mode: 'lead',
      lead_uuid: id,
      onSuccess: () => this.reloadContactReports(),
    });
  },

  async reloadContactReports() {
    const id = leadUuid();
    if (!id) return;
    const res = await O.PREP([await O.mPOST(EP, {lead_uuid: id})]);
    const wrap = U.id('contactReportsHeaderActions');
    if (wrap) delete wrap.dataset.built;
    await this.loadPage(res);
  },

  buildTableHead() {
    const tableHead = U.id('leadContactReportsTableHead');
    if (!tableHead) return;
    tableHead.innerHTML = '';
    const headRow = tableHead.insertRow();
    HEADERS.forEach((label, i) => {
      headRow.insertCell(i).innerHTML = label;
    });
  },

  async buildTableBody(listData, emptyMessage) {
    const tbody = U.id('leadContactReportsTableBody');
    if (!tbody) return;
    tbody.innerHTML = '';
    if (!listData.length) {
      const tr = tbody.insertRow();
      const cell = tr.insertCell(0);
      cell.colSpan = COL_COUNT;
      cell.textContent = emptyMessage || 'No contact reports found.';
      return;
    }
    for (const row of listData) {
      const tr = tbody.insertRow();
      const detailRow = tbody.insertRow();
      detailRow.className = 'contact-report-detail-row hide';
      const detailWrap = detailRow.insertCell(0);
      detailWrap.colSpan = COL_COUNT;
      const detailGrid = document.createElement('div');
      detailGrid.className = 'contact-report-detail-grid';
      addDetailBlock(detailGrid, 'Telephone call outcome', row.telephonic_outcome);
      addDetailBlock(detailGrid, 'Notes', row.notes);
      addDetailBlock(detailGrid, 'Follow-up action', row.follow_up_action);
      addDetailBlock(detailGrid, 'Reason follow up is longer than 2 weeks', row.follow_up_more_than_2_weeks);
      addDetailBlock(detailGrid, 'Reason to retire', row.reason_to_retire);
      detailWrap.appendChild(detailGrid);
      const openDetail = () => toggleDetailRow(detailRow);

      tr.insertCell(0).textContent = await dateText(row.date_of_contact);
      tr.insertCell(1).textContent = cellText(row.medium_of_contact) || '—';
      tr.insertCell(2).textContent = cellText(row.motivation_of_contact) || '—';
      fillPreviewCell(tr.insertCell(3), row.notes, openDetail);
      tr.insertCell(4).textContent = await dateText(row.follow_up_date);
      fillPreviewCell(tr.insertCell(5), row.follow_up_action, openDetail);
      tr.insertCell(6).textContent = ynText(row.retire_advisor_record);
      fillPreviewCell(tr.insertCell(7), row.reason_to_retire, openDetail);

      const detailCell = tr.insertCell(8);
      detailCell.className = 'advisor-icon-cell';
      const detailIcon = svg.detail();
      detailIcon.classList.add('cursor-pointer', 'text-primary');
      detailIcon.style.cursor = 'pointer';
      detailIcon.setAttribute('role', 'button');
      detailIcon.setAttribute('title', 'Detail');
      detailIcon.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openDetail();
      });
      detailCell.appendChild(detailIcon);
    }
  },
};
